import { useState, type ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  CalendarCheck,
  Clock,
  Users,
  ClipboardCheck,
  Globe,
  PenLine,
} from 'lucide-react';
import { instructorApi } from '../api/instructor';
import { useAuthStore } from '../store/auth';
import { useUiStore } from '../store/ui';
import PhoneLink from '../components/PhoneLink';
import dayjs, { fmtDayLong, fmtTime, initials } from '../lib/dates';
import { InstructorLesson } from '../types';
import LessonActions from '../components/LessonActions';
import AttendanceModal from '../components/AttendanceModal';
import { STATE_CHIP, canSignAttendance, lessonState } from '../lib/lessonStatus';

function StatCard({
  label,
  value,
  hint,
  icon,
  tone,
}: {
  label: string;
  value: string;
  hint: string;
  icon: ReactNode;
  tone: string;
}) {
  return (
    <div className="bg-white rounded-2xl border border-slate-200 p-6 shadow-sm">
      <div className="flex items-center justify-between mb-4">
        <span className="text-xs font-semibold text-slate-500 uppercase tracking-wide">
          {label}
        </span>
        <div className={`w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 ${tone}`}>
          {icon}
        </div>
      </div>
      <div className="text-2xl font-bold text-slate-900">{value}</div>
      <div className="text-sm text-slate-600 mt-0.5">{hint}</div>
    </div>
  );
}

function LessonRow({ lesson, last }: { lesson: InstructorLesson; last: boolean }) {
  const openBilan = useUiStore((s) => s.openBilan);
  const openLivret = useUiStore((s) => s.openLivret);
  const [attendanceOpen, setAttendanceOpen] = useState(false);
  const c = lesson.candidate;
  const name = c ? `${c.firstName} ${c.lastName}` : 'Élève';
  const state = lessonState(lesson);
  const chip = STATE_CHIP[state];
  const done = state === 'honored';
  // Leçon déjà terminée (horaire de fin dépassé) : le report / l'annulation
  // n'ont plus de sens, seul le bilan reste pertinent.
  const isPast = dayjs().isAfter(dayjs(lesson.endsAt));

  // Créneau ouvert à la réservation en ligne (ouvert par le secrétariat).
  if (lesson.isOpenSlot) {
    return (
      <div className="flex gap-4">
        <div className="w-14 flex-shrink-0 text-right">
          <div className="text-sm font-semibold text-slate-900">{fmtTime(lesson.startsAt)}</div>
          <div className="text-xs text-slate-400">{fmtTime(lesson.endsAt)}</div>
        </div>
        <div className="flex flex-col items-center flex-shrink-0">
          <span className="w-3 h-3 rounded-full mt-1 flex-shrink-0 ring-4 bg-green-500 ring-green-100" />
          {!last && <span className="flex-1 w-px bg-slate-200" />}
        </div>
        <div className="flex-1 min-w-0 mb-5 rounded-2xl border-2 border-dashed border-green-400 bg-green-50/60 p-5 flex items-center gap-4">
          <div className="w-10 h-10 rounded-full bg-green-100 text-green-600 flex items-center justify-center flex-shrink-0">
            <Globe size={16} />
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-sm font-semibold text-green-700">Ouvert à la réservation</div>
            <div className="text-xs text-green-600">
              {lesson.durationHours} h · visible dans l'espace élève
            </div>
          </div>
          <span className="text-xs text-green-600 font-medium flex-shrink-0">Non réservé</span>
        </div>
      </div>
    );
  }

  return (
    <div className="flex gap-4">
      <div className="w-14 flex-shrink-0 text-right">
        <div className="text-sm font-semibold text-slate-900">{fmtTime(lesson.startsAt)}</div>
        <div className="text-xs text-slate-400">{fmtTime(lesson.endsAt)}</div>
      </div>
      <div className="flex flex-col items-center flex-shrink-0">
        <span
          className={`w-3 h-3 rounded-full mt-1 flex-shrink-0 ring-4 ${
            done
              ? 'bg-green-500 ring-green-100'
              : 'bg-indigo-600 ring-indigo-100'
          }`}
        />
        {!last && <span className="flex-1 w-px bg-slate-200" />}
      </div>
      <div
        className={`flex-1 min-w-0 mb-5 rounded-2xl border p-5 ${
          done ? 'border-slate-200 bg-white' : 'border-indigo-200 bg-indigo-50/40'
        }`}
      >
        <div className="flex flex-wrap items-center gap-3">
          <div className="w-10 h-10 rounded-full bg-gradient-to-br from-indigo-500 to-indigo-600 text-white flex items-center justify-center text-xs font-semibold flex-shrink-0">
            {c ? initials(c.firstName, c.lastName) : '–'}
          </div>
          <div className="min-w-0 flex-1">
            <div className="text-sm font-semibold text-slate-900">{name}</div>
            <div className="text-xs text-slate-500">
              {lesson.lessonType ?? 'Conduite'} · {lesson.durationHours} h
              {c ? ` · ${c.vehicleType === 'automatic' ? 'Boîte auto' : 'Boîte manuelle'}` : ''}
            </div>
            {c && <PhoneLink phone={c.phoneNumber} name={name} />}
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            {lesson.bookedOnline && (
              <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-green-50 text-green-700 border border-green-200 text-xs font-semibold">
                <span className="w-1.5 h-1.5 rounded-full bg-green-500" />
                Réservé en ligne
              </span>
            )}
            <span
              className={`px-2.5 py-1 rounded-full border text-xs font-semibold ${chip.className}`}
            >
              {chip.label}
            </span>
          </div>
        </div>
        <div className="mt-4 flex flex-wrap items-center gap-2">
          {c && <button onClick={()=>openLivret(c.id)} className="px-4 py-2.5 border border-indigo-200 text-indigo-700 hover:bg-indigo-50 text-sm font-semibold rounded-lg">Préparer le cours · derniers objectifs</button>}
          {/* Émargement : présence de l'élève (leçon commencée, pas encore honorée). */}
          {canSignAttendance(lesson) && (
            <button
              onClick={() => setAttendanceOpen(true)}
              className="px-4 py-2.5 border border-indigo-200 text-indigo-700 hover:bg-indigo-50 text-sm font-semibold rounded-lg transition-colors flex items-center gap-2"
            >
              <PenLine size={15} />
              Émargement
            </button>
          )}
          {isPast && c && (
            <button
              onClick={() =>
                openBilan({ candidateId: c.id, candidateName: name, lessonId: lesson.id })
              }
              className="px-4 py-2.5 bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-semibold rounded-lg shadow-sm transition-colors flex items-center gap-2"
            >
              <ClipboardCheck size={15} />
              {lesson.hasBilan ? 'Compléter le bilan' : 'Faire le bilan de leçon'}
            </button>
          )}
          {!isPast && <LessonActions lesson={lesson} />}
        </div>
        {attendanceOpen && (
          <AttendanceModal lesson={lesson} onClose={() => setAttendanceOpen(false)} />
        )}
      </div>
    </div>
  );
}

export default function TodayView() {
  const user = useAuthStore((s) => s.user);
  const setSection=useUiStore(s=>s.setSection);
  const { data, isLoading, isError, error } = useQuery({
    queryKey: ['today',user?.id],
    queryFn: instructorApi.getToday,
  });

  if (isLoading) {
    return <p className="text-slate-500">Chargement de votre journée…</p>;
  }
  if (isError) {
    return (
      <p className="text-red-600">
        Impossible de charger votre journée : {(error as Error).message}
      </p>
    );
  }
  if (!data) return null;

  const { stats, lessons } = data;

  return (
    <section className="space-y-6">
      <div>
        <h1 className="text-slate-900">Bonjour {user?.firstName ?? ''} 👋</h1>
        <p className="text-slate-600">Voici votre journée du {fmtDayLong(data.date)}.</p>
      </div>

      <div className="flex flex-wrap gap-3"><button className="rounded-xl border bg-white px-4 py-3" onClick={()=>setSection('messages')}>Ouvrir la messagerie</button><button className="rounded-xl border bg-white px-4 py-3" onClick={()=>setSection('equipe')}>Notes et réunions partagées</button><span className="rounded-xl border bg-white px-4 py-3">0 tâche · Suivi à venir</span></div>
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <StatCard
          label="Leçons aujourd'hui"
          value={String(stats.lessonsToday)}
          hint={
            data.openSlotsToday > 0
              ? `${data.openSlotsToday} créneau(x) ouvert(s) à la réservation`
              : stats.lessonsToday > 0
                ? 'séance(s) planifiée(s)'
                : 'Aucune leçon'
          }
          icon={<CalendarCheck size={17} className="text-indigo-600" />}
          tone="bg-indigo-50"
        />
        <StatCard
          label="Heures ce mois"
          value={`${stats.hoursThisMonth} h`}
          hint="Heures de conduite réalisées"
          icon={<Clock size={17} className="text-green-600" />}
          tone="bg-green-50"
        />
        <StatCard
          label="Élèves suivis"
          value={String(stats.studentsCount)}
          hint={
            stats.readyForExamCount > 0
              ? `dont ${stats.readyForExamCount} prêt(s) pour l'examen`
              : 'aucun prêt pour l\'examen'
          }
          icon={<Users size={17} className="text-purple-600" />}
          tone="bg-purple-50"
        />
      </div>

      <div className="bg-white rounded-2xl border border-slate-200 p-6 shadow-sm">
        <h2 className="text-slate-900 mb-1">Ma journée</h2>
        <p className="text-sm text-slate-500 mb-6">
          {lessons.length
            ? 'Faites le bilan à la fin de chaque leçon.'
            : 'Aucune leçon prévue aujourd\'hui.'}
        </p>
        <div className="space-y-0">
          {lessons.map((lesson, i) => (
            <LessonRow key={lesson.id} lesson={lesson} last={i === lessons.length - 1} />
          ))}
        </div>
      </div>
    </section>
  );
}
